import type { EnglishIntensity } from '@orborix/shared-types';
import type { CefrLevel } from '../../lib/englishApi.js';
import { INTENSITY_OPTIONS, intensityById, intensityIndex, levelById } from './levelData.js';
import { IconCheck, IconWand } from './icons.js';

interface IntensityControlProps {
  /** Nivel CEFR — para el copy "recomendado para tu nivel X". */
  levelId: CefrLevel;
  /** Intensidad efectiva actual (cómo habla el coach). */
  value: EnglishIntensity;
  /** Intensidad recomendada por el backend — sólo guía visual. */
  recommended: EnglishIntensity;
  /** Toggle global de ajuste automático (`levelAuto`). */
  auto: boolean;
  /** Elegir una intensidad a mano (implica pasar a manual). */
  onChange: (id: EnglishIntensity) => void;
  onToggleAuto: (auto: boolean) => void;
  variant?: 'full' | 'compact';
}

/**
 * Control de "cómo me habla el coach". La intensidad EFECTIVA es `value`
 * siempre; la `recommended` se muestra como guía (badge "rec." + nudge). El
 * toggle auto/manual mapea a `levelAuto` global. Regla de oro: la recomendada
 * SIEMPRE está a la vista. Ver docs/speak-plan/10 §5 (Fase D).
 */
export function IntensityControl({
  levelId,
  value,
  recommended,
  auto,
  onChange,
  onToggleAuto,
  variant = 'full',
}: IntensityControlProps) {
  const cur = intensityById(value);
  const recOpt = intensityById(recommended);
  const curIdx = intensityIndex(value);
  const recIdx = intensityIndex(recommended);
  const diff = value !== recommended;
  const lvl = levelById(levelId);

  return (
    <div className={`onb-intensity ${variant === 'compact' ? 'is-compact' : ''}`}>
      {/* Encabezado: auto/manual */}
      <div className="onb-int-head">
        <div className="onb-int-head-txt">
          <span className="onb-int-eyebrow">CÓMO TE HABLA EL COACH</span>
          <span className="onb-int-mode">
            {auto ? (
              <>
                <IconWand size={14} /> Automático · se ajusta solo
              </>
            ) : (
              <>Manual · vos mandás</>
            )}
          </span>
        </div>
        <AutoManualToggle auto={auto} onToggle={onToggleAuto} />
      </div>

      {/* Rail de 6 paradas */}
      <div className="onb-int-rail" role="radiogroup" aria-label="Intensidad">
        <div className="onb-int-rail-line" />
        <div
          className="onb-int-rail-fill"
          style={{ width: `${(curIdx / (INTENSITY_OPTIONS.length - 1)) * 100}%` }}
        />
        {INTENSITY_OPTIONS.map((o, i) => {
          const isSel = o.id === value;
          const isRec = o.id === recommended;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={isSel}
              className={`onb-int-stop ${isSel ? 'is-sel' : ''} ${isRec ? 'is-rec' : ''} ${i <= curIdx ? 'is-filled' : ''}`}
              onClick={() => onChange(o.id)}
              title={o.label}
            >
              <span className="onb-int-node" />
              <span className="onb-int-stop-label">{o.label}</span>
              {isRec && <span className="onb-int-rec-tag">rec.</span>}
            </button>
          );
        })}
      </div>

      {/* Descripción de la parada activa */}
      <div className="onb-int-desc">
        <div className="onb-int-desc-top">
          <span className="script onb-int-desc-name">{cur.label}</span>
          {cur.l1Support && <span className="onb-int-es-chip">+ español de apoyo</span>}
        </div>
        <p>{cur.desc}</p>
      </div>

      {/* Estado: elección ≠ recomendada (con tacto) / auto */}
      {diff ? (
        <div className="onb-int-nudge">
          <div className="onb-int-nudge-row">
            <span className="onb-int-nudge-pill is-yours">
              <span className="dot" /> {auto ? 'Ahora' : 'Tu elección'} · <b>{cur.label}</b>
            </span>
            <span className="onb-int-nudge-pill is-rec">
              <span className="dot" /> Recomendado · <b>{recOpt.label}</b>
            </span>
          </div>
          <button className="onb-int-nudge-back" onClick={() => onChange(recommended)}>
            {curIdx > recIdx ? 'Está exigente — ' : 'Podés subirle — '}
            ir a lo recomendado
          </button>
        </div>
      ) : auto ? (
        <div className="onb-int-auto-note">
          <IconWand size={13} />
          <span>
            Seguís la recomendación para <b>{lvl.id}</b>. Si subís de nivel, se ajusta sola.
          </span>
        </div>
      ) : (
        <div className="onb-int-auto-note is-match">
          <IconCheck size={13} />
          <span>Tu elección coincide con lo recomendado.</span>
        </div>
      )}
    </div>
  );
}

interface AutoManualToggleProps {
  auto: boolean;
  onToggle: (auto: boolean) => void;
}

/** Toggle Auto / Manual — segmentado compacto. */
export function AutoManualToggle({ auto, onToggle }: AutoManualToggleProps) {
  return (
    <div className="onb-am-toggle" role="group" aria-label="Auto o manual">
      <button className={`onb-am-opt ${auto ? 'is-on' : ''}`} onClick={() => onToggle(true)} aria-pressed={auto}>
        Auto
      </button>
      <button className={`onb-am-opt ${!auto ? 'is-on' : ''}`} onClick={() => onToggle(false)} aria-pressed={!auto}>
        Manual
      </button>
    </div>
  );
}
